import config from "./../config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { createBaseQueryWithAuthHandling } from "../utils/helpers";

let currentCorrelationId = null;

let isConsoleEnabled = false;

const conditionalLog = (message, data) => {
    if (isConsoleEnabled) {
        if (data !== undefined) {
            console.log(message, data);
        } else {
            console.log(message);
        }
    }
};

const conditionalErrorLog = (message, error) => {
    if (isConsoleEnabled) {
        console.error(message, error);
    }
};

const conditionalWarnLog = (message) => {
    if (isConsoleEnabled) {
        console.warn(message);
    }
};

const generateCorrelationId = () => {
    if (typeof crypto !== 'undefined') {
        const guid = crypto.randomUUID();
        return guid;
    }
    return null;
};

const baseQuery = fetchBaseQuery({
    baseUrl: config.REACT_APP_API_URL,
    credentials: "include",
    prepareHeaders: (headers) => {
        headers.set("Accept", "application/json");
        headers.set("Content-Type", "application/json");
        const apiKey = process.env.REACT_APP_API_KEY;
        if (apiKey) headers.set("apikey", apiKey);

        const initialCorrelationId = generateCorrelationId();
        if (initialCorrelationId) {
            headers.set('x-unum-correlation-id', initialCorrelationId);
            currentCorrelationId = initialCorrelationId;
        }

        return headers;
    },
});

const baseQueryWithRetry = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error) {
        const errorMessage = result.error?.status || "";
        conditionalErrorLog("Initial permissions request failed", errorMessage);
        conditionalLog("Initial permissions request failed with correlation id ", currentCorrelationId);

        const isFetchError = errorMessage === "FETCH_ERROR";
        if (isFetchError) {
            conditionalWarnLog("Detected 'Failed to fetch' in permissions request. Retrying once...");

            await new Promise((resolve) => setTimeout(resolve, 300));

            let retryCorrelationId = generateCorrelationId();

            if (retryCorrelationId) {
                const newArgs = {
                    ...args,
                    headers: {
                        ...args.headers,
                        'x-unum-correlation-id': retryCorrelationId
                    }
                };
                currentCorrelationId = retryCorrelationId;

                result = await baseQuery(newArgs, api, extraOptions);

                if (result.error) {
                    conditionalErrorLog("Retry permissions request also failed:", result.error?.status);
                    conditionalLog("Retry permissions request failed with correlation id ", retryCorrelationId);
                    retryCorrelationId = null;
                    currentCorrelationId = null;
                }
            }
        }
    }

    return result;
};

const baseQueryWithRetryAndAuth = createBaseQueryWithAuthHandling(baseQueryWithRetry);

const userApiSlice = createApi({
    reducerPath: "userApi",
    baseQuery: baseQueryWithRetryAndAuth,
    endpoints: (builder) => ({
        getUser: builder.query({
            query: (arg = {}) => {
                const { customVariables, ...params } = arg;

                if (customVariables?.enableConsole !== undefined) {
                    isConsoleEnabled = customVariables.enableConsole;
                }

                return {
                    url: `/employees/${process.env.REACT_APP_API_VERSION}/permissions${config.REACT_APP_ENV_NAME}`,
                    params: {
                        isUserInternal: 1,
                        PolicyPartyId: null,
                        ...params
                    },
                    meta: {
                        enableConsole: customVariables?.enableConsole || false
                    }
                };
            },
        }),
    }),
});

export const { useGetUserQuery, useLazyGetUserQuery } = userApiSlice;

export default userApiSlice;